import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpenIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const statusTone = (status) => {
  if (['ready_to_launch', 'active', 'completed', 'certificate_awarded'].includes(status)) return 'positive';
  if (['provisioning', 'pending_approval', 'paused'].includes(status)) return 'pending';
  return 'negative';
};

export default function DashboardLearning({
  enrollments, summary, getProgress, getActionLabel, formatDateTime, onOpen, onViewStatus, onExplore
}) {
  const [showAll, setShowAll] = useState(false);
  const visibleEnrollments = showAll ? enrollments : enrollments.slice(0, 4);

  return (
    <section className="learner-panel" aria-labelledby="learner-learning-title">
      <header className="learner-panel-heading">
        <h2 id="learner-learning-title">Your learning</h2>
        <p>{summary.activeCount} active · {summary.averageProgress}% average progress</p>
      </header>
      {enrollments.length ? (
        <div className="learner-learning-list">
          {visibleEnrollments.map(enrollment => {
            const progress = getProgress(enrollment);
            const title = enrollment.course?.programme_name || 'Linked course';
            return (
              <article className="learner-learning-row" key={enrollment.public_id}>
                <div className="learner-course-symbol" aria-hidden="true"><BookOpenIcon /></div>
                <div className="learner-learning-content">
                  <div className="learner-learning-title-row">
                    <h3><Link to={`/courses/${enrollment.course?.id}`}>{title}</Link></h3>
                    <span className={`learner-status learner-status--${statusTone(enrollment.status)}`}>
                      {enrollment.status_display || 'Status pending'}
                    </span>
                  </div>
                  <p className="learner-course-location">{enrollment.course?.campus?.name || 'Campus pending'}</p>
                  <div className="learner-progress-line">
                    <div className="learner-progress-track" role="progressbar" aria-label={`${title} course completion`}
                      aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
                      <span style={{ width: `${progress}%` }} />
                    </div>
                    <strong>{Math.round(progress)}%</strong>
                  </div>
                  <div className="learner-learning-meta">
                    <span>{enrollment.module_completion_count || 0} / {enrollment.module_total_count || '—'} modules</span>
                    <span>{enrollment.last_synced_at
                      ? `Last synced ${formatDateTime(enrollment.last_synced_at)}`
                      : 'Waiting for first sync'}</span>
                  </div>
                  {enrollment.certificate_awarded_at ? (
                    <p className="learner-certificate-date">Certificate awarded {formatDateTime(enrollment.certificate_awarded_at)}</p>
                  ) : null}
                </div>
                <div className="learner-learning-actions">
                  <button type="button" className="learner-button learner-button--gold"
                    aria-label={`${getActionLabel(enrollment)}: ${title}`} onClick={() => onOpen(enrollment)}>
                    {getActionLabel(enrollment)}
                  </button>
                  <button type="button" className="learner-button learner-button--outline"
                    aria-label={`View status: ${title}`} onClick={() => onViewStatus(enrollment)}>
                    View status
                  </button>
                </div>
              </article>
            );
          })}
          {enrollments.length > 4 ? (
            <button type="button" className="learner-list-toggle" aria-expanded={showAll} onClick={() => setShowAll(value => !value)}>
              {showAll ? 'Show fewer courses' : `Show all ${enrollments.length} learning courses`} <ChevronRightIcon />
            </button>
          ) : null}
        </div>
      ) : (
        <div className="learner-empty-state">
          <BookOpenIcon aria-hidden="true" />
          <h3>Your next course starts here</h3>
          <p>No online learning in progress yet.</p>
          <button type="button" className="learner-button learner-button--gold" onClick={onExplore}>Explore recommendations</button>
        </div>
      )}
    </section>
  );
}
